/**
 * Busca o histórico de feedbacks de treino de um aluno (intensidade, cansaço,
 * dor e o recado livre `observations`), do mais recente pro mais antigo.
 *
 * Param `limit`: quando passado, retorna só os N últimos. Como nem toda sessão
 * concluída tem feedback, busca-se `limit * 2` linhas e filtra as sem feedback
 * antes de fatiar — garante N itens reais quando existem.
 *
 * Só funciona com personal autenticado dono do aluno (RLS em workout_feedback).
 */
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { FeedbackHistoryItem } from '@/lib/types';

export function useFeedbackHistory(studentId: string, limit?: number) {
  const { data: feedbacks = [], isLoading, error } = useQuery({
    queryKey: ['feedback-history', studentId, limit],
    queryFn: async () => {
      let q = supabase
        .from('workout_sessions')
        .select(`
          id,
          plan:workout_plans(name),
          feedback:workout_feedback(id, intensity, fatigue, had_pain, pain_location, observations, created_at)
        `)
        .eq('student_id', studentId)
        .not('completed_at', 'is', null)
        .order('completed_at', { ascending: false });

      // Fetch extra rows so the post-filter slice still returns `limit` items
      // even if some sessions have no feedback entry
      if (limit !== undefined) q = q.limit(limit * 2);

      const { data, error } = await q;
      if (error) throw error;

      const mapped = (data ?? [])
        .filter((s: any) => (s.feedback as any[]).length > 0)
        .map((s: any) => {
          const fb = (s.feedback as any[])[0];
          return {
            id: fb.id,
            planName: (s.plan as any)?.name ?? 'Treino',
            date: fb.created_at,
            intensity: fb.intensity,
            fatigue: fb.fatigue,
            hadPain: fb.had_pain,
            painLocation: fb.pain_location,
            observations: fb.observations ?? null,
          } as FeedbackHistoryItem;
        });

      return limit !== undefined ? mapped.slice(0, limit) : mapped;
    },
    enabled: !!studentId,
  });

  return { feedbacks, isLoading, error };
}
